import { createApi } from "@reduxjs/toolkit/query/react";

import { type IApiResponse } from "@/shared/interfaces/api-response.interface";
import type {
  IBoundaryGeometriesPageResponse,
  IBoundaryGeometryResponse,
} from "@/shared/interfaces/boundary-geometry.interface";

import { ApiTags } from "../api-tags.enum";
import customFetchBase from "../custom-fetch-base";

import type {
  ICreateMapLevelUploadUrlRequest,
  IDeleteMapLevelRequest,
  IDuplicateMapLevelRequest,
  IFinalizeMapLevelRequest,
  IArchiveMapLevelRequest,
  IGetMapLevelBoundaryGeometriesParams,
  IGetMapLevelBoundaryParams,
  IGetMapLevelParams,
  IGetMapLevelsParams,
  IImportMapLevelRequest,
  IImportMapLevelResponse,
  IMapLevel,
  IMapLevelBoundary,
  IMapLevelBoundaryListResponse,
  IMapLevelListResponse,
  IMapLevelUploadUrlResponse,
  IResolveMapLevelAutoFitRequest,
  IResolveMapLevelGapFillRequest,
  IResolveMapLevelGeometryResponse,
  IUpdateMapLevelRequest,
  IUpsertMapLevelBoundariesRequest,
} from "./map-levels.interface";

const GEOMETRY_CACHE_TTL_SECONDS = 300;

function mapLevelTag(levelId: string) {
  return { type: ApiTags.MapLevels as const, id: levelId };
}

function mapLevelGeometriesTag(levelId: string) {
  return { type: ApiTags.MapLevels as const, id: `geometries-${levelId}` };
}

const mapLevelsApi = createApi({
  reducerPath: "mapLevelsApi",
  baseQuery: customFetchBase,
  tagTypes: [ApiTags.MapLevels],
  endpoints: (builder) => ({
    createMapLevelUploadUrl: builder.mutation<
      IMapLevelUploadUrlResponse,
      ICreateMapLevelUploadUrlRequest
    >({
      query: ({ districtId, filename, mimetype, size }) => ({
        url: `/district/${districtId}/map-levels/upload-url`,
        method: "POST",
        body: { filename, mimetype, size },
      }),
      transformResponse: (result: IApiResponse<IMapLevelUploadUrlResponse>) => result.data,
    }),
    importMapLevel: builder.mutation<IImportMapLevelResponse, IImportMapLevelRequest>({
      query: ({ districtId, zipFileId }) => ({
        url: `/district/${districtId}/map-levels/import`,
        method: "POST",
        body: { zipFileId },
      }),
      invalidatesTags: [ApiTags.MapLevels],
      transformResponse: (result: IApiResponse<IImportMapLevelResponse>) => result.data,
    }),
    getMapLevels: builder.query<IMapLevelListResponse, IGetMapLevelsParams>({
      query: ({ districtId, view, includeIds, skip, take }) => {
        const params = new URLSearchParams();
        if (view != null) {
          params.set("view", view);
        }
        if (skip != null) {
          params.set("skip", String(skip));
        }
        if (take != null) {
          params.set("take", String(take));
        }
        includeIds?.forEach((id) => {
          params.append("includeIds", id);
        });

        const query = params.toString();
        const querySuffix = query.length > 0 ? `?${query}` : "";
        return {
          url: `/district/${districtId}/map-levels${querySuffix}`,
          method: "GET",
        };
      },
      providesTags: [ApiTags.MapLevels],
      transformResponse: (result: IApiResponse<IMapLevelListResponse>) => result.data,
    }),
    getMapLevel: builder.query<IMapLevel, IGetMapLevelParams>({
      query: ({ districtId, levelId }) => ({
        url: `/district/${districtId}/map-levels/${levelId}`,
        method: "GET",
      }),
      providesTags: (_result, _error, { levelId }) => [mapLevelTag(levelId), ApiTags.MapLevels],
      transformResponse: (result: IApiResponse<IMapLevel>) => result.data,
    }),
    getMapLevelBoundary: builder.query<IMapLevelBoundary, IGetMapLevelBoundaryParams>({
      query: ({ districtId, levelId, boundaryId }) => ({
        url: `/district/${districtId}/map-levels/${levelId}/boundaries/${boundaryId}`,
        method: "GET",
      }),
      providesTags: (_result, _error, { levelId }) => [mapLevelTag(levelId)],
      transformResponse: (result: IApiResponse<IMapLevelBoundary>) => result.data,
    }),
    getMapLevelBoundaryGeometry: builder.query<
      IBoundaryGeometryResponse,
      IGetMapLevelBoundaryParams
    >({
      query: ({ districtId, levelId, boundaryId }) => ({
        url: `/district/${districtId}/map-levels/${levelId}/boundaries/${boundaryId}/geometry`,
        method: "GET",
      }),
      keepUnusedDataFor: GEOMETRY_CACHE_TTL_SECONDS,
      providesTags: (_result, _error, { levelId }) => [mapLevelGeometriesTag(levelId)],
      transformResponse: (result: IApiResponse<IBoundaryGeometryResponse>) => result.data,
    }),
    getMapLevelBoundaryGeometries: builder.query<
      IBoundaryGeometriesPageResponse,
      IGetMapLevelBoundaryGeometriesParams
    >({
      query: ({ districtId, levelId, cursor, limit }) => ({
        url: `/district/${districtId}/map-levels/${levelId}/boundaries/geometries`,
        method: "GET",
        params: {
          ...(cursor != null ? { cursor } : {}),
          ...(limit != null ? { limit } : {}),
        },
      }),
      keepUnusedDataFor: GEOMETRY_CACHE_TTL_SECONDS,
      providesTags: (_result, _error, { levelId }) => [mapLevelGeometriesTag(levelId)],
      transformResponse: (result: IApiResponse<IBoundaryGeometriesPageResponse>) => result.data,
    }),
    updateMapLevel: builder.mutation<IMapLevel, IUpdateMapLevelRequest>({
      query: ({ districtId, levelId, body }) => ({
        url: `/district/${districtId}/map-levels/${levelId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _error, { levelId }) => [mapLevelTag(levelId), ApiTags.MapLevels],
      transformResponse: (result: IApiResponse<IMapLevel>) => result.data,
    }),
    upsertMapLevelBoundaries: builder.mutation<
      IMapLevelBoundaryListResponse,
      IUpsertMapLevelBoundariesRequest
    >({
      query: ({ districtId, levelId, body }) => ({
        url: `/district/${districtId}/map-levels/${levelId}/boundaries`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_result, _error, { levelId }) => [
        mapLevelTag(levelId),
        mapLevelGeometriesTag(levelId),
        ApiTags.MapLevels,
      ],
      transformResponse: (result: IApiResponse<IMapLevelBoundaryListResponse>) => result.data,
    }),
    resolveMapLevelGapFill: builder.mutation<
      IResolveMapLevelGeometryResponse,
      IResolveMapLevelGapFillRequest
    >({
      query: ({ districtId, levelId, body }) => ({
        url: `/district/${districtId}/map-levels/${levelId}/boundaries/gap-fill/resolve`,
        method: "POST",
        body,
      }),
      transformResponse: (result: IApiResponse<IResolveMapLevelGeometryResponse>) => result.data,
    }),
    resolveMapLevelAutoFit: builder.mutation<
      IResolveMapLevelGeometryResponse,
      IResolveMapLevelAutoFitRequest
    >({
      query: ({ districtId, levelId, body }) => ({
        url: `/district/${districtId}/map-levels/${levelId}/boundaries/auto-fit/resolve`,
        method: "POST",
        body,
      }),
      transformResponse: (result: IApiResponse<IResolveMapLevelGeometryResponse>) => result.data,
    }),
    finalizeMapLevel: builder.mutation<IMapLevel, IFinalizeMapLevelRequest>({
      query: ({ districtId, levelId }) => ({
        url: `/district/${districtId}/map-levels/${levelId}/finalize`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { levelId }) => [mapLevelTag(levelId), ApiTags.MapLevels],
      transformResponse: (result: IApiResponse<IMapLevel>) => result.data,
    }),
    archiveMapLevel: builder.mutation<IMapLevel, IArchiveMapLevelRequest>({
      query: ({ districtId, levelId }) => ({
        url: `/district/${districtId}/map-levels/${levelId}/archive`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { levelId }) => [mapLevelTag(levelId), ApiTags.MapLevels],
      transformResponse: (result: IApiResponse<IMapLevel>) => result.data,
    }),
    duplicateMapLevel: builder.mutation<IMapLevel, IDuplicateMapLevelRequest>({
      query: ({ districtId, levelId, body }) => ({
        url: `/district/${districtId}/map-levels/${levelId}/duplicate`,
        method: "POST",
        body: body ?? {},
      }),
      invalidatesTags: [ApiTags.MapLevels],
      transformResponse: (result: IApiResponse<IMapLevel>) => result.data,
    }),
    deleteMapLevel: builder.mutation<IMapLevel, IDeleteMapLevelRequest>({
      query: ({ districtId, levelId }) => ({
        url: `/district/${districtId}/map-levels/${levelId}`,
        method: "DELETE",
      }),
      invalidatesTags: [ApiTags.MapLevels],
      transformResponse: (result: IApiResponse<IMapLevel>) => result.data,
    }),
  }),
});

export const {
  useCreateMapLevelUploadUrlMutation,
  useImportMapLevelMutation,
  useGetMapLevelsQuery,
  useLazyGetMapLevelsQuery,
  useGetMapLevelQuery,
  useLazyGetMapLevelQuery,
  useGetMapLevelBoundaryQuery,
  useLazyGetMapLevelBoundaryQuery,
  useGetMapLevelBoundaryGeometryQuery,
  useLazyGetMapLevelBoundaryGeometryQuery,
  useGetMapLevelBoundaryGeometriesQuery,
  useLazyGetMapLevelBoundaryGeometriesQuery,
  useUpdateMapLevelMutation,
  useUpsertMapLevelBoundariesMutation,
  useResolveMapLevelGapFillMutation,
  useResolveMapLevelAutoFitMutation,
  useFinalizeMapLevelMutation,
  useArchiveMapLevelMutation,
  useDuplicateMapLevelMutation,
  useDeleteMapLevelMutation,
} = mapLevelsApi;

export default mapLevelsApi;
